import { computed, type ComputedRef } from 'vue';

import { useUserStore } from '@bv/stores/user';
import { ensureArray } from '@bv/utils/common/array/ensure-array';

/**
 * Reactive permission checks against the current user store permissions.
 *
 * Prefer this for templates, directives, and reactive UI when permissions are
 * already loaded. For route guards that must load user details first, use
 * `evaluateRouteMetaAccessAsync` (awaits `getUserDetails()` then checks via
 * `hasPermission`).
 *
 * @example
 * ```ts
 * const { can } = usePermission();
 * const canEdit = can('edit-users');
 * const canManage = can(['read-users', 'edit-users']);
 * ```
 */
export const usePermission = (): {
  can: (permission: string | string[]) => ComputedRef<boolean>;
  /** @deprecated Use `can` instead. */
  hasPermission: (permission: string | string[]) => ComputedRef<boolean>;
} => {
  const userStore = useUserStore();

  /**
   * Sync, reactive check over already-loaded `userStore.permissions` (does not fetch).
   * Returns a `ComputedRef<boolean>` that updates when store permissions change.
   *
   * @param permission - The permission or permissions to check.
   * @returns A computed reference to the boolean result.
   * @see evaluateRouteMetaAccessAsync — async route access; awaits `getUserDetails()` then uses this check.
   */
  const can = (permission: string | string[]): ComputedRef<boolean> =>
    computed(() => {
      const required = ensureArray(permission);
      return required.every((p) => userStore.permissions.includes(p));
    });

  /** @deprecated Use `can` instead. */
  const hasPermission = can;

  return { can, hasPermission };
};
